
import { defineComponent } from 'vue';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, IonSpinner, alertController } from '@ionic/vue';
import { arrowBackOutline, refreshOutline, timeOutline, locationOutline, peopleOutline, personAddOutline } from 'ionicons/icons';
import FormSheet from './FormSheet.vue';
import { leadApi, leadApiForm, loadMeta, currentPosition, formatDateTime, optionColor } from '../../salesLeads';

const EMPTY_FEEDBACK = { feedback: '', client_sentiment: '', outcome: '', next_step: '', next_meeting_at: '' };

export default defineComponent({
  name: 'SalesLeadMeeting',
  components: { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, IonSpinner, FormSheet },
  data() {
    return {
      meeting: null as any,
      permissions: {} as any,
      meta: null as any,
      sheet: null as string | null,
      feedbackMode: 'complete',
      busy: false,
      file: null as File | null,
      fbForm: { ...EMPTY_FEEDBACK },
      rescheduleForm: { scheduled_at: '', reason: '' },
      cancelForm: { status: 'Cancelled', reason: '' },
      arrowBackOutline, refreshOutline, timeOutline, locationOutline, peopleOutline, personAddOutline,
    };
  },
  computed: {
    meetingId(): string {
      return this.$route.params.meetingId as string;
    },
  },
  watch: {
    '$route.params.meetingId'(id: string) {
      if (id && this.$route.path.startsWith('/sales-lead-meeting/')) {
        this.load();
      }
    },
  },
  async created() {
    this.meta = await loadMeta().catch(() => null);
    this.load();
  },
  methods: {
    format: formatDateTime,
    sentimentStyle(sentiment: string) {
      const color = optionColor(this.meta, 'sentiment', sentiment);
      return { color, background: color + '1f' };
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push('/sales-lead-meetings');
      }
    },
    load() {
      leadApi('getMeeting', { meeting_id: this.meetingId }, true).then(r => {
        this.meeting = r.data.meeting;
        this.permissions = r.data.permissions;
      }).catch(() => this.$router.replace('/sales-lead-meetings'));
    },
    openFeedback(mode: string) {
      this.feedbackMode = mode;
      this.fbForm = { ...EMPTY_FEEDBACK };
      this.file = null;
      this.sheet = 'feedback';
    },
    onFile(event: Event) {
      const files = (event.target as HTMLInputElement).files;
      this.file = files && files.length ? files[0] : null;
    },
    async info(message: string) {
      const a = await alertController.create({ header: 'Done', message, buttons: ['OK'] });
      await a.present();
    },
    async submitFeedback() {
      if (!this.fbForm.feedback.trim() || !this.fbForm.client_sentiment || !this.fbForm.outcome) {
        return;
      }
      this.busy = true;
      const extra: Record<string, any> = { meeting_id: this.meetingId };
      if (this.feedbackMode === 'complete') {
        Object.assign(extra, await currentPosition());
      }
      leadApiForm(this.feedbackMode === 'complete' ? 'completeMeeting' : 'addMeetingFeedback', { ...extra, ...this.fbForm }, this.file)
        .then(async r => {
          this.busy = false;
          this.sheet = null;
          this.load();
          // A completed Demo/Negotiation meeting suggests moving the lead forward; the rep decides.
          if (r.data.suggested_stage) {
            await this.offerStage(r.data.suggested_stage, r.data.message);
          } else {
            this.info(r.data.message);
          }
        }).catch(() => {
          this.busy = false;
        });
    },
    async offerStage(stage: string, message: string) {
      const a = await alertController.create({
        header: 'Move the lead forward?',
        message: `${message}<br><br>Move the lead to <b>${stage}</b>?`,
        buttons: [
          { text: 'Not now', role: 'cancel' },
          {
            text: 'Move to ' + stage,
            handler: () => {
              leadApi('changeStage', { lead_id: this.meeting.lead.id, stage, note: 'After ' + this.meeting.meeting_type + ' meeting' })
                .then(r => this.info(r.data.message)).catch(() => undefined);
            },
          },
        ],
      });
      await a.present();
    },
    submitReschedule() {
      if (!this.rescheduleForm.scheduled_at || !this.rescheduleForm.reason.trim()) {
        return;
      }
      this.busy = true;
      leadApi('rescheduleMeeting', { meeting_id: this.meetingId, ...this.rescheduleForm }).then(r => {
        this.busy = false;
        this.sheet = null;
        this.info(r.data.message);
        this.$router.replace('/sales-lead-meeting/' + r.data.meeting_id);
      }).catch(() => {
        this.busy = false;
      });
    },
    submitCancel() {
      if (!this.cancelForm.reason.trim()) {
        return;
      }
      this.busy = true;
      leadApi('cancelMeeting', { meeting_id: this.meetingId, ...this.cancelForm }).then(r => {
        this.busy = false;
        this.sheet = null;
        this.info(r.data.message);
        this.load();
      }).catch(() => {
        this.busy = false;
      });
    },
  },
});
